import { BookOpen, LogOut } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';

import { navItems } from '@client/config/routes';

import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '../ui/sidebar';
import { useAuth } from '../../hooks/use-auth';
import { iniciaisDe } from '../../lib/format';

export function AppShell({ children }: { children: ReactNode }) {
  const { usuario, isAdmin, hasFeature, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  async function aoSair() {
    await logout();
    navigate('/login', { replace: true });
  }

  const itensVisiveis = navItems.filter(
    (item) => (!item.adminOnly || isAdmin) && (!item.feature || hasFeature(item.feature)),
  );

  // `Intl.DateTimeFormat` devolve tudo minúsculo; em português só a primeira letra da frase leva maiúscula (o `capitalize` do CSS maiusculizaria o "De").
  const dataFormatada = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  }).format(new Date());
  const hoje = dataFormatada.charAt(0).toUpperCase() + dataFormatada.slice(1);

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader>
          <div className="flex items-center gap-2 px-6 py-7">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white text-primary">
              <BookOpen className="size-4" />
            </span>
            <div className="flex min-w-0 flex-col text-sidebar-foreground">
              <span
                className="truncate text-sm leading-tight font-semibold"
                title={usuario?.nomeEmpresa}
              >
                {usuario?.nomeEmpresa ?? 'KFlow'}
              </span>
              <span className="text-xs leading-tight opacity-70">Gestão Inteligente Kumon</span>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {itensVisiveis.map((item) => {
                const ativo = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.path}>
                    {ativo ? (
                      <span
                        aria-hidden
                        className="absolute inset-y-1 left-0 w-1 rounded-r bg-orange-600"
                      />
                    ) : null}
                    <SidebarMenuButton
                      asChild
                      isActive={ativo}
                      className="h-auto rounded-xl px-3 py-2.5"
                    >
                      <NavLink to={item.path}>
                        {Icon ? <Icon className="size-4" /> : null}
                        <span>{item.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton size="lg">
                <Avatar className="size-6">
                  <AvatarFallback className="bg-white text-primary">
                    {usuario ? iniciaisDe(usuario.nome) : ''}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{usuario?.nome}</span>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              <DropdownMenuItem onSelect={() => void aoSair()}>
                <LogOut className="size-4" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>

      {/* `asChild`: evita um segundo landmark <main> (só um por documento). */}
      <SidebarInset asChild>
        <div>
          <header className="flex items-center justify-between gap-4 border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="-ml-1" />
              <span className="text-sm text-muted-foreground">{hoje}</span>
            </div>
            <div className="flex items-center gap-4">
              <Avatar className="size-8">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {usuario ? iniciaisDe(usuario.nome) : ''}
                </AvatarFallback>
              </Avatar>
            </div>
          </header>
          <main className="flex-1 p-6">{children}</main>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
